import { LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import { Input } from 'antd'
import { isCypress } from 'app/lib/services/EnvironmentService'
import { debounce } from 'lodash'
import * as React from 'react'
import { useIntl } from 'react-intl'

import FormItem from '../UI-Elements/DataEntry/Form/FormItem'
import { InputRef } from '../UI-Elements/DataEntry/Form/Input'

const { Search } = Input
type ChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | React.MouseEvent<HTMLElement>
  | React.KeyboardEvent<HTMLInputElement>
type Props = React.ComponentProps<typeof Search> & {
  recordType?: string
  showLabel?: boolean
  searchDelay?: number
}

const prefix = 'component.data_entry.search'

const KSearch: React.FC<Props> = ({
  recordType,
  showLabel,
  enterButton,
  loading,
  allowClear = true,
  searchDelay = 400,
  onSearch,
  ...others
}) => {
  const { formatMessage } = useIntl()
  const onDelayedSearch = debounce((q: string, e: ChangeEvent) => onSearch?.(q, e), searchDelay)
  const suffix = loading ? <LoadingOutlined /> : <SearchOutlined />
  const ref = React.useRef<InputRef>()

  React.useEffect(() => {
    if (ref.current && others.autoFocus && !loading && !isCypress) {
      ref.current.focus?.()
    }
  }, [ref, loading])

  const getLabel = () => formatMessage({ id: `${prefix}.label.${recordType || 'default'}` })

  const getPlaceholder = () =>
    formatMessage({
      id: `${prefix}.placeholder.${recordType || 'default'}`,
      defaultMessage: formatMessage({ id: `${prefix}.placeholder.default` })
    })

  const renderSearch = () => (
    <Choose>
      <When condition={enterButton}>
        <Search
          placeholder={getPlaceholder()}
          loading={loading}
          onSearch={onSearch}
          allowClear={allowClear}
          ref={ref}
          {...others}
        />
      </When>
      <Otherwise>
        <Input
          placeholder={getPlaceholder()}
          onChange={(e) => onDelayedSearch(e.target.value, e)}
          suffix={suffix}
          allowClear={allowClear}
          ref={ref}
          {...others}
        />
      </Otherwise>
    </Choose>
  )

  return (
    <Choose>
      <When condition={showLabel}>
        <FormItem labelCol={{ xs: 24 }} labelAlign="left" label={getLabel()}>
          {renderSearch()}
        </FormItem>
      </When>
      <Otherwise>{renderSearch()}</Otherwise>
    </Choose>
  )
}

export default KSearch
